import {useCallback, useMemo} from 'react';

import {UseTreeData} from 'modules/decisionsNext/hooks/trees/types';

import {useClientAgencyCodesTreeQuery} from 'modules/decisionsNext/api/trees/clientAgencyCodesTree.generated';
import {ClientAgencyCodesFilterKeys} from 'core/constants/policyFilter';
import {GenericTreeNode, groupSelectedKeys, ungroupSelectedKeys, iterateTree} from 'utils/treeUtils';

import {SkipQueryParams, StoreAdapterParams, TreeBasicNode} from 'components/Tree/TreeTypes';
import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';

export type ClientAgencyTreeNode = GenericTreeNode<
  TreeBasicNode & {
    __typename?: 'AgencyCodeCategory' | 'AgencyCode';
  }
>;

export const useClientAgencyCodesTree: UseTreeData<string, ClientAgencyTreeNode> = ({skip}: SkipQueryParams = {}) => {
  const {data, loading} = useClientAgencyCodesTreeQuery({
    skip,
    disableSkipSetsLoadingState: true,
  });
  const treeData = useMemo<ClientAgencyTreeNode[]>(() => data?.sourceCodes.clientAgencyCodes ?? [], [data]);

  return {
    loading,
    treeData,
    treeMap: useMemo(() => {
      const map = new Map<string, ClientAgencyTreeNode>();

      iterateTree(
        treeData,
        (node, parent) => {
          map.set(node.key, {
            ...node,
            children: undefined,
            parentKey: parent?.key,
          });
        },
        {withParent: true}
      );

      return map;
    }, [treeData]),
  };
};

export const useClientAgencyCodesTreeSelection = ({
  store,
  setStore,
  skip,
}: StoreAdapterParams<ClientAgencyCodesFilterKeys> & SkipQueryParams) => {
  const {treeData, loading} = useClientAgencyCodesTree({skip});

  const {clientAgencyCodes} = store;
  const selected = useMemo(
    () => Array.from(ungroupSelectedKeys(treeData, clientAgencyCodes ?? [])),
    [treeData, clientAgencyCodes]
  );

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const groupedSelected = Array.from(groupSelectedKeys(treeData, newSelected));
      setStore({
        clientAgencyCodes: groupedSelected.length ? groupedSelected : undefined,
      });
    },
    [setStore, treeData]
  );

  const setSelectedNone = useCallback(() => {
    setStore({
      clientAgencyCodes: undefined,
    });
  }, [setStore]);

  return {
    ...useOptimizedSelection({
      selected,
      setSelected,
      setSelectedNone,
      treeData,
      groupSelection: true,
    }),
    selectedNoneData: {
      clientAgencyCodes: undefined,
    },
    treeData,
    isLoading: loading,
  };
};
